import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, SectionTitle, Note, Image, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider, Separator, Result, CorrectionBox } from '../../../../components/course/CourseComponents';
import screen1 from '../repere2.jpg';

const Chapitre2Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Affichage et animation de la tête du serpent + déplacement automatique et contrôle au clavier
        </p>
      </InfoBox>

      <Separator type="text" text="Affichage et déplacement" color="gray-darker" size="md" />
      <SectionTitle>🎯 1/ Affichage et déplacement de la tête</SectionTitle>

      <p>On va maintenant afficher la tête du serpent et la faire avancer automatiquement.</p>

      <p>Pour cela, on va créer deux variables globales pour stocker les coordonnées de la tête :</p>

      <CodeBlock language="javascript">
{`let posX, posY;`}
      </CodeBlock>

      <p>On a aussi besoin de deux variables pour définir la direction de déplacement du serpent :</p>

      <CodeBlock language="javascript">
{`let dx = 1, dy = 0;`}
      </CodeBlock>

      <ul className="list-disc pl-6 mb-4">
        <li><InlineCode>dx</InlineCode> = direction sur l'axe des X → indique le sens horizontal du déplacement
          <ul className="list-disc pl-6 mt-2">
            <li><InlineCode>dx = 1</InlineCode> : le serpent va vers la droite</li>
            <li><InlineCode>dx = -1</InlineCode> : il va vers la gauche</li>
            <li><InlineCode>dx = 0</InlineCode> : il ne bouge pas horizontalement</li>
          </ul>
        </li>
        <li><InlineCode>dy</InlineCode> = direction sur l'axe des Y → indique le sens vertical du déplacement
          <ul className="list-disc pl-6 mt-2">
            <li><InlineCode>dy = 1</InlineCode> : le serpent descend</li>
            <li><InlineCode>dy = -1</InlineCode> : il monte</li>
            <li><InlineCode>dy = 0</InlineCode> : il ne bouge pas verticalement</li>
          </ul>
        </li>
      </ul>

      <p>Donc <InlineCode>dx = 1</InlineCode> et <InlineCode>dy = 0</InlineCode> veut dire que le serpent avance vers la droite, de manière horizontale.</p>

      <p>Ensuite, on crée une variable globale vitesse qui permettra de définir la vitesse de déplacement du serpent :</p>

      <CodeBlock language="javascript">
{`let vitesse = 270; // vitesse du snake en pixels par seconde`}
      </CodeBlock>

      <p>Cette variable peut être ajustée pour équilibrer le jeu.</p>

      <p>Dans <InlineCode>setup()</InlineCode>, on initialise la position de départ de la tête : on commence à gauche, au milieu de l'écran :</p>

      <CodeBlock language="javascript">
{`posX = 40;
posY = height / 2;`}
      </CodeBlock>

      <p>Dans la fonction <InlineCode>draw()</InlineCode>, on calcule la nouvelle position de la tête (posX, posY) en fonction de la direction (dx, dy), de la vitesse et du deltaTime pour que ce soit indépendant du framerate :</p>

      <CodeBlock language="javascript">
{`let dt = deltaTime / 1000;
posX = posX + dx * vitesse * dt;
posY = posY + dy * vitesse * dt;`}
      </CodeBlock>

      <p>Enfin, on affiche l'image de la tête en posX, posY :</p>

      <CodeBlock language="javascript">
{`image(imageTete, posX, posY);`}
      </CodeBlock>

      <Note type="tip" title="⚠️ Point de pivot des images">
        <p>Pour le fond (background), on laisse le point d'origine en haut à gauche.</p>
        <p>Mais pour tous les autres éléments du jeu, on définit le point d'origine au centre de l'image, pour faciliter l'affichage et les collisions :</p>
        <CodeBlock language="javascript">
{`imageMode(CENTER);`}
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer : afficher et déplacer la tête du serpent">
        <p>- Commence par créer toutes les variables globales nécessaires :</p>

        <CodeBlock language="javascript">
{`let vitesse = 270; // vitesse du snake en pixels par seconde
let posX, posY;
let dx = 1, dy = 0;`}
        </CodeBlock>

        <p>- Dans <InlineCode>setup()</InlineCode>, définis la position de départ :</p>

        <CodeBlock language="javascript">
{`posX = 40;
posY = height / 2;`}
        </CodeBlock>

        <p>- Dans <InlineCode>draw()</InlineCode> :</p>
        <ul className="list-disc pl-6 mb-4">
          <li>Passe en <InlineCode>imageMode(CENTER)</InlineCode></li>
          <li>Calcule la nouvelle position (posX, posY) en fonction de la direction (dx, dy), de la vitesse et du deltaTime pour que ce soit indépendant du framerate</li>
          <li>Affiche <InlineCode>imageTete1</InlineCode> à cette position</li>
        </ul>

        <CodeBlock language="javascript">
{`imageMode(CENTER);
let dt = deltaTime / 1000;
posX = posX + dx * vitesse * dt;
posY = posY + dy * vitesse * dt;
image(imageTete1, posX, posY);`}
        </CodeBlock>

        <br/>
        <Result>La tête du serpent se déplace de gauche à droite automatiquement à une vitesse de 270 pixels par seconde.</Result>
      

      <CorrectionBox title="Correction :">
        <CodeBlock language="javascript">
{`//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(500, 500);
  posX = 40;
  posY = height / 2;
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  imageMode(CORNER);
  background(imageFond);
  imageMode(CENTER);

  let dt = deltaTime / 1000;
  posX = posX + dx * vitesse * dt;
  posY = posY + dy * vitesse * dt;

  image(imageTete1, posX, posY);
}`}
        </CodeBlock>
      </CorrectionBox>

      </Note>

      <Separator type="text" text="Gestion du clavier" color="gray-darker" size="md" />
      <SectionTitle>⌨️ 2/ Gestion du clavier et changement de direction</SectionTitle>

      <p>On veut maintenant permettre au joueur de changer la direction du serpent en appuyant sur les flèches du clavier.</p>

      <p>Pour cela, on utilise la fonction spéciale de p5.js appelée à chaque fois qu'une touche est pressée :</p>

      <CodeBlock language="javascript">
{`function keyPressed() {
  // ...
}`}
      </CodeBlock>

      <p>Comme <InlineCode>mouseX</InlineCode> et <InlineCode>mouseY</InlineCode> indiquent la position de la souris, <InlineCode>keyCode</InlineCode> indique quelle touche a été pressée.</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Variable</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
          <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>keyCode</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Code de la touche pressée</td>
          </tr>

          </tbody>
        </table>
      </TableContainer>

      <p>Voici quelques codes utiles :</p>

      <ul className="list-disc pl-6 mb-4">
        <li>Flèche gauche : <InlineCode>LEFT_ARROW</InlineCode></li>
        <li>Flèche droite : <InlineCode>RIGHT_ARROW</InlineCode></li>
        <li>Flèche haut : <InlineCode>UP_ARROW</InlineCode></li>
        <li>Flèche bas : <InlineCode>DOWN_ARROW</InlineCode></li>
      </ul>

            <p>Voici le comportement que l'on souhaite coder :</p>

      <ul className="list-disc pl-6 mb-4">
        <li>Si on appuie sur la flèche droite, alors on définit <InlineCode>dx = 1</InlineCode> et <InlineCode>dy = 0</InlineCode></li>
        <li>Si on appuie sur la flèche gauche, alors <InlineCode>dx = -1</InlineCode> et <InlineCode>dy = 0</InlineCode></li>
        <li>Si on appuie sur la flèche haut, alors <InlineCode>dy = -1</InlineCode> et <InlineCode>dx = 0</InlineCode></li>
        <li>Si on appuie sur la flèche bas, alors <InlineCode>dy = 1</InlineCode> et <InlineCode>dx = 0</InlineCode></li>
      </ul>

      <Note type="warning" title="🚨 Y inversé !">
        <p>N'oublie pas : dans p5.js, l'axe des Y est inversé :</p>
        <ul className="list-disc pl-6">
          <li>Y augmente vers le bas</li>
          <li>Y diminue vers le haut</li>
        </ul>
        <Image src={screen1} alt="Axe Y inversé" width="370px" height="260px" borderRadius="xl" shadow={true} border={false} className="object-contain" />
      </Note>

            <p>Ce qui donne en code :</p>

      <CodeBlock language="javascript">
{`function keyPressed() {
  if (keyCode === UP_ARROW) { dx = 0; dy = -1; } 
  else if (keyCode === DOWN_ARROW) { dx = 0; dy = 1; } 
  else if (keyCode === LEFT_ARROW) { dx = -1; dy = 0; } 
  else if (keyCode === RIGHT_ARROW) { dx = 1; dy = 0; }
}`}
      </CodeBlock>

      <Note type="tip" title="🚫 Pas de retour en arrière">
        <p>On aimerait ajouter un dernier comportement lié aux entrées du joueur : on ne souhaite pas que le serpent puisse changer brusquement de direction.</p>
        <p>Par exemple, s'il avance vers la gauche, il ne doit pas pouvoir repartir directement vers la droite, sinon il se toucherait lui-même et le joueur perdrait instantanément.</p>
        <p>Pour éviter ce comportement, on autorise le changement de direction uniquement si la direction opposée n'est pas actuellement active.</p>
      </Note>

      <p>Par exemple :</p>
      <p>Si on appuie sur la flèche gauche et que le serpent ne va pas vers la droite, alors on autorise le changement vers la gauche.</p>

      <CodeBlock language="javascript">
{`if (keyCode === LEFT_ARROW && dx !== 1) { dx = -1; dy = 0; }`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : ajoute la gestion des touches">
        <p>Dans la fonction <InlineCode>keyPressed()</InlineCode>, teste les touches directionnelles et change la direction du serpent en conséquence :</p>

        <CodeBlock language="javascript">
{`//--------------------------------- CLAVIER ------------------------------------------
function keyPressed() {
  if (keyCode === UP_ARROW && dy !== 1) { dx = 0; dy = -1; }
  else if (keyCode === DOWN_ARROW && dy !== -1) { dx = 0; dy = 1; }
  else if (keyCode === LEFT_ARROW && dx !== 1) { dx = -1; dy = 0; }
  else if (keyCode === RIGHT_ARROW && dx !== -1) { dx = 1; dy = 0; }
}`}
        </CodeBlock>

        <Result>Le serpent change de direction quand le joueur appuie sur une flèche, sauf si cela le ferait revenir en arrière.</Result>
      </Note>

      <Separator type="text" text="Animation" color="gray-darker" size="md" />
      <SectionTitle>👁️ 3/ Animation de la tête</SectionTitle>

      <p>Pour finir, on va faire cligner les yeux du serpent en alternant deux images :</p>

      <ul className="list-disc pl-6 mb-4">
        <li><InlineCode>imageTete1</InlineCode> (yeux ouverts)</li>
        <li><InlineCode>imageTete2</InlineCode> (yeux fermés)</li>
      </ul>

   
        <p>Pour cela, on va utiliser la variable <InlineCode>frameCount</InlineCode> de p5.js, qui s'incrémente à chaque frame.</p>
        <p>On souhaite effectuer un calcul qui renvoie un nombre entre 0 et 99 en fonction de frameCount. Ainsi, si le résultat est inférieur à 10, on affiche la tête avec les yeux fermés ; sinon, on affiche la tête normale.</p>
   
      <Note type="tip" title="💡 Modulo %">
        <p>En mathématiques, il existe l'opération modulo, qui permet de renvoyer le reste de la division euclidienne.</p>
        <p>Par exemple : <InlineCode>frameCount % 100</InlineCode> renvoie le reste de la division de frameCount par 100.</p>
      </Note>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 text-sm">
          <thead>
            <tr>
              <th className="border border-gray-300 dark:border-gray-600 p-1 bg-gray-100 dark:bg-gray-800 text-xs">frameCount</th>
              <th className="border border-gray-300 dark:border-gray-600 p-1 bg-gray-100 dark:bg-gray-800 text-xs">frameCount % 100</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">0</td>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">0</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">1</td>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">1</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">99</td>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">99</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">100</td>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">0</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">101</td>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">1</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">149</td>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">49</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">200</td>
              <td className="border border-gray-300 dark:border-gray-600 p-1 text-xs">0</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <p>Si <InlineCode>frameCount % 100 &gt; 10</InlineCode>, on affiche la tête normale (la plupart du temps). Sinon, on affiche la tête avec les yeux fermés pendant 10 frames.</p>

      <p>En code, cela donne :</p>
      <CodeBlock language="javascript">
{`let imageTete;
if (frameCount % 100 > 10) imageTete = imageTete1;
else imageTete = imageTete2;`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : fais cligner les yeux">
        <p>Dans <InlineCode>draw()</InlineCode>, au lieu d'afficher systématiquement imageTete1, utilise frameCount % 100 pour afficher soit imageTete1, soit imageTete2 :</p>

        <CodeBlock language="javascript">
{`let imageTete;
if (frameCount % 100 > 10) imageTete = imageTete1;
else imageTete = imageTete2;

image(imageTete, posX, posY);`}
        </CodeBlock>

        <Result>Le serpent cligne des yeux automatiquement de temps en temps.</Result>
      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : Animation de la tête + déplacement</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 4 : Snake
let imageFond;
let imageTete1;
let imageTete2;
let imageTeteMort;
let imageCorps;
let imagePomme;
let sonMort;
let sonCroque

let vitesse = 270; // vitesse du snake en pixels par seconde

let posX, posY;
let dx = 1, dy = 0;

//--------------------------------- PRELOAD ------------------------------------------
function preload(){
  imageFond = loadImage('fond.png')
  imageTete1 = loadImage('snake-tete1.png');
  imageTete2 = loadImage('snake-tete2.png');
  imageTeteMort = loadImage('snake-tete-mort.png');
  imageCorps = loadImage('snake-corps.png');
  imagePomme = loadImage('pomme.png');
  sonMort = loadSound('mort.wav')
  sonCroque = loadSound('croque.wav')
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(500, 500);
  posX = 40;
  posY = height / 2;
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  imageMode(CORNER);
  background(imageFond);
  imageMode(CENTER);

  // Déplacement
  let dt = deltaTime / 1000;
  posX = posX + dx * vitesse * dt;
  posY = posY + dy * vitesse * dt;

  // Affichage et animation de la tête
  let imageTete;
  if (frameCount % 100 > 10)  imageTete = imageTete1;
  else imageTete = imageTete2;
  image(imageTete, posX, posY);
}

//--------------------------------- CLAVIER ------------------------------------------
function keyPressed() {
  if (keyCode === UP_ARROW && dy !== 1) { dx = 0; dy = -1; }
  else if (keyCode === DOWN_ARROW && dy !== -1) { dx = 0; dy = 1; }
  else if (keyCode === LEFT_ARROW && dx !== 1) { dx = -1; dy = 0; }
  else if (keyCode === RIGHT_ARROW && dx !== -1) { dx = 1; dy = 0; }
}`}
      </CodeBlock>

      <p>🎯 Dans le prochain chapitre, nous allons ajouter le corps du serpent qui suit la tête !</p>
    </>
  );
};

export default Chapitre2Content; 

